// Wie viel Pause wird abgezogen?
//
// Die Leitung gibt eine Staffel vor, die App rechnet danach. Vorbild ist §4
// ArbZG: ab mehr als 6 Stunden 30 Minuten, ab mehr als 9 Stunden 45 Minuten.
//
// ══ Warum überhaupt eine Regel ══
//
// Vorher tippte der Mitarbeiter seine Pause beim Ausstempeln selbst ein. Das ist
// Tipparbeit am Handy mit Handschuhen, es ist uneinheitlich — und wer die Pause
// weglässt, verschafft sich Arbeitszeit. Eine Regel macht daraus etwas, das für
// alle gleich gilt und niemand vergessen kann.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export interface Pausenstufe {
  /** Ab welcher Arbeitsdauer (in Minuten) diese Stufe greift. */
  ab_minuten: number;
  pause_minuten: number;
}

/** Fällt die Einstellung aus, gilt das Gesetz — nicht "gar keine Pause". */
export const GESETZ: Pausenstufe[] = [
  { ab_minuten: 360, pause_minuten: 30 },
  { ab_minuten: 540, pause_minuten: 45 },
];

/**
 * Wie viel Pause zieht die Regel bei dieser Arbeitsdauer ab?
 *
 * Es greift die HÖCHSTE zutreffende Stufe: Wer 10 Stunden arbeitet, bekommt 45
 * Minuten abgezogen, nicht 30 + 45. Genau dieser Fehler ist der naheliegende,
 * wenn man die Stufen aufsummiert.
 *
 * Die Grenze ist "mehr als", nicht "mindestens": Bei exakt 6 Stunden schreibt
 * das Gesetz noch keine Pause vor.
 */
export function pauseFuer(arbeitsMinuten: number, stufen: Pausenstufe[] = GESETZ): number {
  let pause = 0;
  for (const s of stufen) {
    if (arbeitsMinuten > s.ab_minuten && s.pause_minuten > pause) pause = s.pause_minuten;
  }
  // Nie mehr abziehen, als überhaupt gearbeitet wurde — sonst entstünde eine
  // negative Arbeitszeit, und die taucht dann im Lohn-Export auf.
  return Math.min(pause, arbeitsMinuten);
}

export function usePausenregel() {
  return useQuery({
    queryKey: ["pausenregel"],
    staleTime: 60_000,
    networkMode: "always",
    queryFn: async (): Promise<Pausenstufe[]> => {
      const { data, error } = await supabase
        .from("app_settings")
        .select("value")
        .eq("key", "pausenregel")
        .maybeSingle();
      if (error) throw error;
      const stufen = (data?.value as { stufen?: Pausenstufe[] } | null)?.stufen;
      return Array.isArray(stufen) ? stufen : GESETZ;
    },
  });
}

export function useSpeicherePausenregel() {
  const qc = useQueryClient();
  return useMutation({
    networkMode: "always",
    mutationFn: async (stufen: Pausenstufe[]) => {
      // Sortiert ablegen: Die Anzeige liest die Liste unverändert vor, und eine
      // durcheinandergeratene Staffel liest sich wie ein Fehler.
      const sortiert = [...stufen].sort((a, b) => a.ab_minuten - b.ab_minuten);
      const { error } = await supabase
        .from("app_settings")
        .update({ value: { stufen: sortiert } })
        .eq("key", "pausenregel");
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pausenregel"] }),
  });
}
